import * as React from 'react';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
import GeneralFormGroup, { FormGroupProps } from '../input/GeneralFormGroup';
import { FormError, Omit, Validate } from '../../../typings/typings';
import { Intent } from '@blueprintjs/core';
import { TLCode } from '../../../common/enums';
import { createGetErrorText, makeValidate, notEmpty } from '../../../common/validateUtils';
import withTranslate, { Translate, WithTranslateProps } from '../WithTranslate';

export interface BaseFormFieldProps
  extends Omit<FieldProps<any>, 'validate'>,
    Omit<FormGroupProps, 'children' | 'labelFor' | 'labelInfo'> {
  validate?: Validate | Validate[];
  required?: boolean;
  inline?: boolean;
}

interface FormFieldProps extends BaseFormFieldProps {
  children: (props: { input: any }) => any;
}

const RequiredErrorCode: TLCode = TLCode.FORM_FIELD_VALIDATION_REQUIRED;

interface ErrorProps {
  errors: FormError[];
  translate: Translate;
}

const Error: React.SFC<ErrorProps> = (props) => {
  const { errors, translate } = props;

  const getErrorText = createGetErrorText(translate);

  if (!errors || !errors.length) {
    return null;
  }

  const renderSingleError = (error: FormError) => {
    return <span>{getErrorText(error)}</span>;
  };

  if (errors.includes(RequiredErrorCode)) {
    return renderSingleError(RequiredErrorCode);
  }

  if (typeof errors === 'string') {
    return renderSingleError(errors);
  }

  return (
    <span>
      {errors.map((error, i) => (
        <React.Fragment key={i}>
          {renderSingleError(error)}
          <br />
        </React.Fragment>
      ))}
    </span>
  );
};

class FormField extends React.Component<FormFieldProps & WithTranslateProps> {
  createValidation = (props: FormFieldProps = this.props) => {
    const { required, validate: validateFromProps } = props;

    const validateFromPropsArray = Array.isArray(validateFromProps) ? validateFromProps : [validateFromProps];

    return makeValidate([...validateFromPropsArray, required && notEmpty].filter(Boolean));
  };

  render() {
    const {
      allowNull,
      children,
      component,
      format,
      isEqual,
      name,
      parse,
      render,
      subscription,
      validate,
      validateFields,
      value,
      disabled,
      helperText,
      inline,
      intent,
      label,
      labelInfo,
      required,
      centerLabelVertically,
      type,
      className,
      translate,
      ...inputProps
    } = this.props;

    return (
      <Field
        allowNull={allowNull}
        name={name}
        format={format}
        isEqual={isEqual}
        parse={parse}
        subscription={subscription}
        validate={this.createValidation()}
        validateFields={validateFields}
        value={value}
        type={type}
      >
        {({ input, meta }: FieldRenderProps<any>) => {
          const hasErrors = meta.error && meta.error.length > 0;
          const shouldDisplayErrors = hasErrors && meta.touched;
          const renderHelperText = () => {
            return shouldDisplayErrors ? <Error errors={meta.error} translate={this.props.translate} /> : helperText;
          };

          return (
            <GeneralFormGroup
              labelFor={name}
              label={label}
              className={className}
              helperText={renderHelperText()}
              intent={shouldDisplayErrors ? Intent.DANGER : Intent.NONE}
              disabled={disabled}
              labelInfo={required}
              inline={inline}
              fill={this.props.fill}
              centerLabelVertically={centerLabelVertically}
            >
              {this.props.children({ input: { ...inputProps, ...input, required } })}
            </GeneralFormGroup>
          );
        }}
      </Field>
    );
  }
}

export default withTranslate(FormField);
